import { API_BASE } from "../api/base";
/**
 * SDK di tracciamento proprietario: raccoglie gli eventi e li manda al nostro
 * collector, sul nostro dominio.
 *
 * Perché esiste, accanto a GA4: gli script di Google sono bloccati da una quota
 * consistente di browser (ad blocker, modalità privacy), quindi i dati di GA4
 * sono sistematicamente incompleti; e ogni modifica al tracciamento richiede di
 * passare da un'interfaccia esterna. Questo canale è first-party e sotto il
 * nostro controllo.
 *
 * Tre principi non negoziabili:
 *  1. non deve MAI rompere la pagina — ogni percorso è in try/catch e un errore
 *     di rete viene ignorato in silenzio;
 *  2. non deve rallentare — gli eventi partono in lotti, fuori dal percorso
 *     critico, con `sendBeacon` quando la pagina si sta chiudendo;
 *  3. non parte nulla senza consenso, esattamente come per GA4.
 */

const COLLECT_PATH = "/api/v1/analytics/collect";
const CONSENT_KEY = "gb_cookie_consent";
const ANON_KEY = "gb_anon_id";
const SESSION_KEY = "gb_session_key";
const SESSION_TS_KEY = "gb_session_ts";
const UTM_KEY = "gb_session_utm";

/** Stessa convenzione del collector e di GA4, così i numeri sono confrontabili. */
const SESSION_IDLE_MS = 30 * 60 * 1000;
const FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUE = 50;
/** Oltre questa soglia si smette di accodare: un ciclo impazzito non deve
 *  riempire la memoria del browser né sommergere il collector. */
const HARD_CAP = 200;

type Primitive = string | number | boolean | null;

interface QueuedEvent {
  event_id: string;
  event_name: string;
  event_version: number;
  occurred_at: string;
  page_path: string;
  page_title?: string;
  referrer?: string;
  locale?: string;
  value_cents?: number;
  currency?: string;
  properties?: Record<string, Primitive>;
}

let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let started = false;
let droppedSinceLastFlush = 0;
/** Quante page_view sono state accodate: serve a capire se quella della
 *  pagina d'ingresso è andata persa perché il consenso non c'era ancora. */
let pageViewsEmitted = 0;

// ── Utilità di base, tutte difensive ────────────────────────────────────────
function uuid(): string {
  try {
    if (crypto?.randomUUID) return crypto.randomUUID();
  } catch { /* ambienti senza crypto */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function read(store: Storage | undefined, key: string): string | null {
  try { return store?.getItem(key) ?? null; } catch { return null; }
}
function write(store: Storage | undefined, key: string, value: string): void {
  try { store?.setItem(key, value); } catch { /* storage pieno o bloccato */ }
}

export function hasConsent(): boolean {
  return read(window.localStorage, CONSENT_KEY) === "granted";
}

/** Identificatore casuale e opaco, cancellabile dall'utente. Nessun fingerprinting. */
function anonymousId(): string {
  let id = read(window.localStorage, ANON_KEY);
  if (!id) {
    id = uuid();
    write(window.localStorage, ANON_KEY, id);
  }
  return id;
}

/** Chiave di sessione, rinnovata dopo 30 minuti di inattività. */
function sessionKey(): string {
  const now = Date.now();
  const last = Number(read(window.sessionStorage, SESSION_TS_KEY) || 0);
  let key = read(window.sessionStorage, SESSION_KEY);
  if (!key || !last || now - last > SESSION_IDLE_MS) {
    key = uuid();
    write(window.sessionStorage, SESSION_KEY, key);
  }
  write(window.sessionStorage, SESSION_TS_KEY, String(now));
  return key;
}

/**
 * Contesto d'ingresso, letto UNA VOLTA al caricamento del modulo.
 *
 * Va catturato qui e non al momento dell'invio: il sito è una SPA e il primo
 * invio avviene diversi secondi dopo, quando l'utente ha già accettato i cookie
 * e magari cambiato pagina. A quel punto la query string con i parametri di
 * campagna non c'è più e l'attribuzione è persa per sempre — cioè proprio il
 * dato per cui si fanno le campagne.
 *
 * Resta in memoria, non su disco: prima del consenso non si scrive nulla sul
 * dispositivo. Viene persistito in sessionStorage solo quando il consenso
 * arriva, così sopravvive ai ricaricamenti di pagina.
 */
/** Identificatori di clic aggiunti dalle piattaforme pubblicitarie.
 *  Si registra solo QUALE era presente, mai il valore: serve a capire che il
 *  traffico è a pagamento, non a identificare la persona che ha cliccato. */
const CLICK_ID_KEYS = [
  "gclid", "gbraid", "wbraid", "msclkid", "fbclid", "li_fat_id", "ttclid", "twclid",
];

const ENTRY = (() => {
  const utm: Record<string, string> = {};
  const clickIds: Record<string, string> = {};
  let path = "/";
  let referrer = "";
  try {
    const params = new URLSearchParams(window.location.search);
    ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "utm_id"].forEach((k) => {
      const v = params.get(k);
      if (v) utm[k] = v.slice(0, 120);
    });
    CLICK_ID_KEYS.forEach((k) => {
      // "1" e non il valore: al server basta sapere che c'era.
      if (params.get(k)) clickIds[k] = "1";
    });
    path = window.location.pathname + window.location.search;
    referrer = document.referrer || "";
  } catch { /* ambiente senza DOM */ }
  return { utm, clickIds, path, referrer };
})();

function captureUtm(): Record<string, string> {
  // Una visita successiva nella stessa sessione non ha più i parametri
  // nell'URL: si riusano quelli memorizzati all'ingresso.
  const saved = read(window.sessionStorage, UTM_KEY);
  if (saved) {
    try { return JSON.parse(saved) as Record<string, string>; } catch { /* json rotto */ }
  }
  if (Object.keys(ENTRY.utm).length) {
    write(window.sessionStorage, UTM_KEY, JSON.stringify(ENTRY.utm));
    return ENTRY.utm;
  }
  return {};
}

// ── Invio ───────────────────────────────────────────────────────────────────
function buildBody(events: QueuedEvent[]): string {
  return JSON.stringify({
    anonymous_id: anonymousId(),
    session_key: sessionKey(),
    consent_analytics: true,
    environment: import.meta.env.MODE === "production" ? "production" : "development",
    app_version: import.meta.env.VITE_APP_VERSION ?? null,
    utm: captureUtm(),
    click_ids: ENTRY.clickIds,
    // Pagina d'ingresso dichiarata dal browser: il server non deve dedurla
    // dal primo evento che arriva, che può essere già della seconda pagina.
    landing_path: ENTRY.path,
    entry_referrer: ENTRY.referrer || undefined,
    viewport_width: window.innerWidth,
    events,
  });
}

/**
 * @param beacon usa `sendBeacon`, l'unico invio che il browser porta a termine
 *   mentre la pagina si sta chiudendo. Con `fetch` normale l'ultimo lotto —
 *   quello che contiene l'uscita dal sito e spesso la conversione — si perde.
 */
function send(events: QueuedEvent[], beacon: boolean): void {
  if (!events.length) return;
  const url = `${API_BASE}${COLLECT_PATH}`;
  const body = buildBody(events);
  try {
    if (beacon && navigator.sendBeacon) {
      const ok = navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
      if (ok) return;
      // sendBeacon rifiuta oltre il proprio limite di dimensione: si ripiega su fetch.
    }
    void fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "omit",
    }).catch(() => { /* il collector non deve mai disturbare l'utente */ });
  } catch { /* nessun canale disponibile: si rinuncia a questo lotto */ }
}

function flush(beacon = false): void {
  if (!queue.length) return;
  const batch = queue.splice(0, MAX_QUEUE);
  if (droppedSinceLastFlush > 0) droppedSinceLastFlush = 0;
  send(batch, beacon);
}

function scheduleFlush(): void {
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    flush(false);
  }, FLUSH_INTERVAL_MS);
}

// ── API pubblica ────────────────────────────────────────────────────────────
export function track(
  eventName: string,
  properties?: Record<string, Primitive | undefined>,
  extra?: { valueCents?: number; currency?: string },
): void {
  try {
    if (!hasConsent()) return; // stessa barriera di GA4: prima del consenso, nulla
    if (queue.length >= HARD_CAP) { droppedSinceLastFlush++; return; }

    const clean: Record<string, Primitive> = {};
    if (properties) {
      for (const [k, v] of Object.entries(properties)) {
        if (v !== undefined) clean[k] = v as Primitive;
      }
    }

    queue.push({
      event_id: uuid(),
      event_name: eventName,
      event_version: 1,
      occurred_at: new Date().toISOString(),
      page_path: window.location.pathname + window.location.search,
      page_title: document.title?.slice(0, 300),
      referrer: document.referrer || undefined,
      locale: document.documentElement.lang || undefined,
      value_cents: extra?.valueCents,
      currency: extra?.currency,
      properties: clean,
    });

    // Gli eventi economici non aspettano il timer: se l'utente chiude subito
    // dopo l'acquisto, quel dato non deve andare perso.
    if (eventName === "page_view") pageViewsEmitted++;
    if (eventName === "purchase") flush(true);
    else scheduleFlush();
  } catch { /* il tracciamento non interrompe mai il flusso applicativo */ }
}

/** Da chiamare una sola volta all'avvio dell'applicazione. */
export function startTracker(): void {
  if (started) return;
  started = true;
  try {
    // Ultimo lotto quando la pagina viene nascosta o chiusa. `pagehide` copre
    // anche il ritorno dalla cache di navigazione su Safari, dove `unload` non parte.
    const finalFlush = () => flush(true);
    window.addEventListener("pagehide", finalFlush);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") finalFlush();
    });
  } catch { /* ambiente senza DOM */ }
}

/**
 * Da chiamare subito dopo l'accettazione dei cookie.
 *
 * La visualizzazione della pagina d'ingresso viene emessa al montaggio, quando
 * il consenso ancora non c'è, e va quindi perduta: senza questo recupero la
 * prima pagina di ogni visita non risulterebbe mai, e la pagina d'ingresso
 * registrata sarebbe la seconda.
 */
export function onConsentGranted(): void {
  try {
    if (pageViewsEmitted > 0) return; // già registrata, non raddoppiare
    track("page_view");
  } catch {
    /* noop */
  }
}

/** Alla revoca del consenso: coda svuotata e identificatori cancellati. */
export function resetTracker(): void {
  try {
    queue = [];
    pageViewsEmitted = 0;
    if (timer) { clearTimeout(timer); timer = null; }
    window.localStorage.removeItem(ANON_KEY);
    window.sessionStorage.removeItem(SESSION_KEY);
    window.sessionStorage.removeItem(SESSION_TS_KEY);
    window.sessionStorage.removeItem(UTM_KEY);
  } catch { /* noop */ }
}

/** Solo per i test end-to-end: forza l'invio immediato. */
export function flushNow(): void {
  flush(true);
}

/**
 * Identificatori da allegare a un ordine o a una richiesta.
 *
 * Restituisce oggetto vuoto senza consenso: nulla deve essere inviato prima
 * che sia stato dato, nemmeno accodato a una richiesta che parte comunque.
 */
export function analyticsIds(): { analytics_anonymous_id?: string; analytics_session_key?: string } {
  if (!hasConsent()) return {};
  try {
    return { analytics_anonymous_id: anonymousId(), analytics_session_key: sessionKey() };
  } catch {
    return {};
  }
}

// Un solo collegamento per utente per caricamento di pagina. In una
// applicazione a pagina singola il componente di autenticazione si rimonta di
// continuo: senza questo, `identify` partirebbe a ogni cambio di pagina.
let identifiedUser: string | null = null;

/**
 * Dichiara che chi sta navigando è l'utente ora autenticato.
 *
 * Da chiamare dopo un login o una registrazione riusciti, e prima del
 * pagamento. Senza, un ordine resta scollegato dalla navigazione che lo ha
 * preceduto e risulta per sempre senza provenienza.
 *
 * **Non invia l'identificativo dell'utente.** Il server lo ricava dal token di
 * autenticazione: accettarlo dal browser permetterebbe a chiunque di attribuire
 * la propria navigazione a un'altra persona.
 *
 * Non solleva e non attende: un difetto del tracciamento non deve poter
 * interrompere un accesso o un pagamento.
 */
export function identify(
  userKey: string,
  accessToken: string,
  method: "login" | "register" | "checkout" = "login",
): void {
  if (!hasConsent() || !userKey || !accessToken) return;
  const marker = `${userKey}:${method === "checkout" ? "checkout" : "auth"}`;
  if (identifiedUser === marker) return;
  identifiedUser = marker;

  try {
    const token = accessToken;
    void fetch(`${API_BASE}/api/v1/analytics/identify`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        anonymous_id: anonymousId(),
        session_key: sessionKey(),
        method,
      }),
      keepalive: true,
    }).catch(() => {
      // Un collegamento mancato è un dato mancante, non un errore da mostrare:
      // reimpostare il marcatore permette di riprovare al prossimo montaggio.
      identifiedUser = null;
    });
  } catch {
    identifiedUser = null;
  }
}

/** Alla disconnessione: il prossimo accesso deve poter ricollegare. */
export function clearIdentity(): void {
  identifiedUser = null;
}
